import { OverlayRef } from '@angular/cdk/overlay';
import { StaticProvider, Type } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { ModalConfig } from '../services/modal.service';

/**
 * Contains all information about a modal
 */
export class ModalRef {
  /**
   * Content of the modal (string or component)
   */
  private _content: string|Type<any>;

  private onContentChangeSubject = new Subject<string|Type<any>>();
  private onWantsCloseSubject = new Subject<{reason: ModalCloseReason, cancelClose: () => void, close: () => void}>();
  private onCloseSubject = new Subject<ModalCloseReason>();

  private _buttonsLoading = [false, false, false, false];
  private _buttonsDisabled = [false, false, false, false];

  /**
   * Indicates which modal buttons are showing a loading animation (true = loading).
   *
   * `ModalButton` enum is used as index to access the individual buttons.
   *
   * Note: Prefer the method `setButtonLoading` to manipulate the loading state.
   */
  public get buttonsLoading() {
    return this._buttonsLoading;
  }

  /**
   * Indicates which modal buttons are disabled (true = disabled).
   *
   * `ModalButton` enum is used as index to access the individual buttons.
   *
   * Note: Preferthe method `setButtonDisabled` or `setAllButtonsDisabled` to manipulate the disabled state.
   */
  public get buttonsDisabled() {
    return this._buttonsDisabled;
  }

  /**
   * Emits the new content if the modal content was changed
   */
  public get onContentChange(): Observable<string|Type<any>> {
    return this.onContentChangeSubject.asObservable();
  }

  /**
   * Emits the close reason (and completes) if the modal is closed.
   */
  public get onClose(): Observable<ModalCloseReason> {
    return this.onCloseSubject.asObservable();
  }

  /**
   * Emits if the modal is about to close.
   * The close process can be cancelled using the function `cancelClose` and continued by using the `close` function.
   * Both functions are part of the emitted object (in addition to the close reason).
   *
   * Only use `cancelClose` if an async task needs to be done.
   * If all tasks are synchronous in the handler function (`onWantsClose.subscribe`), `cancelClose` must not be called.
   *
   * Properties of the emitted object:
   * - reason: The close reason
   * - cancelClose: Call this function to cancel the close process (i.e. the modal will not close)
   * - close: Call this function to continue the close process with the same reason
   *   (e.g. after some async work is finished for which `cancelClose` was called)
   */
  public get onWantsClose(): Observable<ModalWantsCloseParams> {
    return this.onWantsCloseSubject.asObservable();
  }

  public get content() {
    return this._content;
  }

  public set content(value: string|Type<any>) {
    this._content = value;
    this.onContentChangeSubject.next(value);
  }

  /**
   * @param overlayRef Reference to the overlay displaying the modal
   * @param modalConfig Basic settings of the modal
   */
  constructor(
    public readonly overlayRef: OverlayRef,
    public readonly modalConfig: ModalConfig,
    /**
     * The FormGroup of the modal.
     *
     * All controls contained in the modal content can be added to this FormGroup. For example, this makes validation easier.
     * 
     * ### Usage (example implementation, see `src/app/shared/modals/modaluntil/modaluntil.component.ts`)
     * 
     * In your modal content Component, it is recommended to define a `FormGroup` that is a child of the `modalForm`:
     * 
     * ```typescript
     * public formGroup: FormGroup;
     * 
     * ngOnInit(): void {
     *   this.formGroup = new FormGroup({
     *     myTextInput: new FormControl('Default value')
     *   });
     *   this.modalRef.modalForm.addControl('groupname', this.formGroup);
     * }
     * ```
     * 
     * In your template, add the `formGroup` to the element that wraps all controls and
     * add `formControlName` to each control which is part of the formGroup.
     * ```html
     * <div [formGroup]="formGroup">
     *   <input type="text" formControlName="myTextInput">
     * </div>
     * ```
     */
    public readonly modalForm: FormGroup
  ) {
    this._content = modalConfig.content;

    if (this.modalConfig.closeOnBackdropClick) {
      this.overlayRef.backdropClick().subscribe(() => {
        // If the default buttons is currently disabled, backdropClick should not close the modal
        if (this.modalConfig.buttons !== ModalButtons.None
            && (this.buttonsDisabled[this.modalConfig.defaultCancelButton] || this.buttonsLoading[this.modalConfig.defaultCancelButton])) {
          return;
        }

        // Only choose BackdropClick as reason if there are no buttons
        // (if possible, the default button is the reason if the backdrop was clicked)
        let reason = ModalCloseReason.BackdropClick;
        if (this.modalConfig.buttons !== ModalButtons.None) {
          reason = getModalCloseReasonFromButton(this.modalConfig.defaultCancelButton);
        }

        this.close(reason);
      });
    }
  }

  /**
   * Closes the modal
   * @param reason Reason that caused the modal to close (e.g. button click)
   */
  public close(reason: ModalCloseReason) {
    /**
     * Close modal after onWantsClose is finished without interruption or if the modal is allowed to close after closing was interrupted
     */
    const finalClose = () => {
      this.onCloseSubject.next(reason);

      // Complete all observables
      this.onContentChangeSubject.complete();
      this.onWantsCloseSubject.complete();
      this.onCloseSubject.complete();

      this.overlayRef.detach();
    };

    // If reason is a BackdropClick, the defaultButton is returned as reason (if any button exists)
    if (reason === ModalCloseReason.BackdropClick && this.modalConfig.buttons) {
      reason = getModalCloseReasonFromButton(this.modalConfig.defaultCancelButton);
    }

    // Is set to true if any subscriber of onWantsClose calls `cancelClose`
    let closeCancelled = false;

    this.onWantsCloseSubject.next({
      reason,
      cancelClose: () => {
        closeCancelled = true;
      },
      close: () => {
        finalClose();
      }
    });

    if (!closeCancelled) {
      finalClose();
    }
  }

  /**
   * Show/hide loading animation on the given button(s).
   *
   * `toggleDisabled` controls whether all buttons are disabled (if `loading` = true) or enabled (if `loading` = false).
   * Disabling all buttons prevents the user from closing the modal while in loading state.
   * When loading gets disabled but the buttons are not reenabled (`toggleDisabled = false`),
   * then the button which was in loading state will stay disabled.
   *
   * If the default button is in loading state or disabled, the close button and backdrop click are also disabled.
   * @param buttons Buttons on which the loading animation is shown/hidden
   * @param loading Show (true) or hide (false) the loading animation
   * @param toggleDisabled If true, the other buttons are disabled if loading is true or enabled if loading is false
   */
  public setButtonLoading(buttons: ModalButton[]|ModalButton, loading: boolean = true, toggleDisabled: boolean = false) {
    const buttonsArr = [].concat(buttons);

    buttonsArr.forEach((button) => {
      this.buttonsLoading[button] = loading;
    });

    if (toggleDisabled) {
      allModalButtons.forEach((button) => {
        this.buttonsDisabled[button] = loading;
      });
    }
  }

  /**
   * Disables/enables the given button(s).
   *
   * If the default button is disabled, the close button and backdrop click are also disabled.
   * @param buttons Buttons which are disabled/enabled
   * @param disabled Disable (true) or enable (false) the given button(s)
   */
  public setButtonDisabled(buttons: ModalButton[]|ModalButton, disabled: boolean = true) {
    const buttonsArr = [].concat(buttons);

    buttonsArr.forEach((button) => {
      this.buttonsDisabled[button] = disabled;
    });
  }

  /**
   * Disables/enables all buttons.
   *
   * If the default button is disabled, the close button and backdrop click are also disabled.
   * @param disabled Disable (true) or enable (false) all button(s)
   */
  public setAllButtonsDisabled(disabled: boolean = true) {
    this.setButtonDisabled(allModalButtons, disabled);
  }
}

/**
 * Returns close reason that corresponds to the given ModalButton.
 *
 * Note that the return value is arbitrary if the modal does not have buttons (ModalButtons.None)
 */
export function getModalCloseReasonFromButton(button: ModalButton) {
  switch (button) {
    case ModalButton.Ok:
      return ModalCloseReason.Ok;
    case ModalButton.Cancel:
      return ModalCloseReason.Cancel;
    case ModalButton.Yes:
      return ModalCloseReason.Yes;
    case ModalButton.No:
      return ModalCloseReason.No;
  }
}

/**
 * Buttons that are shown at the bottom of the Modal
 */
export enum ModalButtons {
  None,
  Ok,
  OkCancel,
  YesNo,
  YesNoCancel
}

/**
 * Enumeration of all buttons that could be shown at the bottom of the Modal
 */
export enum ModalButton {
  Ok,
  Cancel,
  Yes,
  No
}

/**
 * All buttons that a modal could contain.
 */
export const allModalButtons = [ModalButton.Ok, ModalButton.Cancel, ModalButton.Yes, ModalButton.No];

/**
 * Reason that caused the modal to close
 */
export enum ModalCloseReason {
  /**
   * Backdrop or close button clicked
   * (only if modal does not have buttons).
   * If the modal has any buttons, the default button is chosen as reason)
   */
  BackdropClick,
  Ok,
  Cancel,
  Yes,
  No,
  /**
   * Reason is unknown (most likely none of the other reasons match)
   */
  Other
}

/**
 * Icon that is shown next to the MessageBox text
 */
export enum MessageBoxIcon {
  None,
  Question,
  Information,
  Warning,
  Error
}

export type ModalWantsCloseParams = {reason: ModalCloseReason, cancelClose: () => void, close: () => void};
